import React, { useEffect, useState } from "react";
import { Box, Button, Chip, IconButton, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { Add, Remove } from "@mui/icons-material";
import { Helmet } from "react-helmet-async";
import { useProductDetailSectionStyles } from "./ProductDetailSection.styles";
import { useI18n } from "../../../../i8n/useI18n";
import { useCommerceData } from "../../../../hooks/useCommerceData";
import { useCart } from "../../../../hooks/useCart";
import { commerceRepository } from "../../../../api";
import { formatMoney } from "../../../../utils/formatPrice";

interface ProductDetailSectionProps {
    handle: string;
}

const ProductDetailSection = (props: ProductDetailSectionProps) => {
    const { classes } = useProductDetailSectionStyles();
    const { t } = useI18n();
    const { addItem, isMutating } = useCart();

    const { data: product, loading } = useCommerceData(
        () => commerceRepository.getProductByHandle(props.handle),
        [props.handle]
    );

    const [activeImageIndex, setActiveImageIndex] = useState(0);
    const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);
    const [quantity, setQuantity] = useState(1);
    const [justAdded, setJustAdded] = useState(false);

    useEffect(() => {
        if (product) {
            setSelectedVariantId(product.variants.find((v) => v.availableForSale)?.id ?? product.variants[0]?.id ?? null);
            setActiveImageIndex(0);
            setQuantity(1);
        }
    }, [product]);

    if (loading) {
        return null;
    }

    if (!product) {
        return (
            <Box className={classes.root}>
                <Box className={classes.container}>
                    <Typography>{t("common.error")}</Typography>
                </Box>
            </Box>
        );
    }

    const selectedVariant = product.variants.find((v) => v.id === selectedVariantId) ?? product.variants[0];
    const optionName = product.options[0]?.name ?? t("productPage.variantLabel");

    const handleAddToCart = async () => {
        if (!selectedVariant) return;
        await addItem(selectedVariant.id, quantity);
        setJustAdded(true);
        setTimeout(() => setJustAdded(false), 2000);
    };

    return (
        <Box className={classes.root}>
            <Helmet>
                <title>{product.title} | {t("brand.name")}</title>
                <meta name="description" content={product.description} />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Product",
                        name: product.title,
                        description: product.description,
                        image: product.images.map((image) => image.url),
                        sku: selectedVariant?.sku ?? undefined,
                        brand: { "@type": "Brand", name: product.vendor },
                        offers: {
                            "@type": "Offer",
                            priceCurrency: selectedVariant?.price.currencyCode,
                            price: selectedVariant?.price.amount,
                            availability: selectedVariant?.availableForSale
                                ? "https://schema.org/InStock"
                                : "https://schema.org/OutOfStock",
                        },
                    })}
                </script>
            </Helmet>
            <Box className={classes.container}>
                <Box className={classes.breadcrumb}>
                    <Typography component={RouterLink} to={t("links.shop")} className={classes.breadcrumbLink}>
                        {t("productPage.breadcrumbShop")}
                    </Typography>
                    <span>/</span>
                    <Typography>{product.title}</Typography>
                </Box>

                <Box className={classes.layout}>
                    <Box>
                        <Box className={classes.mainImageWrapper}>
                            {product.images[activeImageIndex] && (
                                <Box
                                    component="img"
                                    src={product.images[activeImageIndex].url}
                                    alt={product.images[activeImageIndex].altText ?? product.title}
                                    className={classes.mainImage}
                                />
                            )}
                        </Box>
                        {product.images.length > 1 && (
                            <Box className={classes.thumbRow}>
                                {product.images.map((image, index) => (
                                    <Box
                                        key={image.url}
                                        className={`${classes.thumb} ${index === activeImageIndex ? classes.thumbActive : ""}`}
                                        onClick={() => setActiveImageIndex(index)}
                                    >
                                        <Box component="img" src={image.url} alt="" className={classes.thumbImage} />
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>

                    <Box>
                        <Typography className={classes.vendor}>{product.vendor}</Typography>
                        <Typography variant="h2" className={classes.title}>{product.title}</Typography>

                        <Box className={classes.priceRow}>
                            <Typography variant="h4" className={classes.price}>
                                {selectedVariant && formatMoney(selectedVariant.price)}
                            </Typography>
                            {selectedVariant?.compareAtPrice && (
                                <Typography className={classes.compareAtPrice}>
                                    {formatMoney(selectedVariant.compareAtPrice)}
                                </Typography>
                            )}
                        </Box>

                        <Typography
                            className={`${classes.availability} ${
                                selectedVariant?.availableForSale ? classes.inStock : classes.outOfStock
                            }`}
                        >
                            {selectedVariant?.availableForSale
                                ? selectedVariant.quantityAvailable <= 5
                                    ? t("productPage.lowStock")
                                    : t("productPage.inStock")
                                : t("productPage.outOfStock")}
                        </Typography>

                        {product.options[0]?.values.length > 1 && (
                            <Box className={classes.optionGroup}>
                                <Typography className={classes.optionLabel}>{optionName}</Typography>
                                <Box className={classes.optionChips}>
                                    {product.variants.map((variant) => (
                                        <Chip
                                            key={variant.id}
                                            label={variant.title}
                                            clickable
                                            disabled={!variant.availableForSale}
                                            color={variant.id === selectedVariantId ? "primary" : "default"}
                                            variant={variant.id === selectedVariantId ? "filled" : "outlined"}
                                            onClick={() => setSelectedVariantId(variant.id)}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}

                        <Box className={classes.quantityRow}>
                            <Typography className={classes.optionLabel} sx={{ mb: 0 }}>{t("productPage.quantityLabel")}</Typography>
                            <Box className={classes.quantityControl}>
                                <IconButton
                                    className={classes.quantityButton}
                                    size="small"
                                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                                >
                                    <Remove fontSize="small" />
                                </IconButton>
                                <Typography className={classes.quantityValue}>{quantity}</Typography>
                                <IconButton className={classes.quantityButton} size="small" onClick={() => setQuantity((q) => q + 1)}>
                                    <Add fontSize="small" />
                                </IconButton>
                            </Box>
                        </Box>

                        <Box className={classes.addToCartRow}>
                            <Button
                                variant="contained"
                                size="large"
                                disabled={!selectedVariant?.availableForSale || isMutating}
                                onClick={handleAddToCart}
                            >
                                {justAdded ? t("productPage.addedToCart") : t("productPage.addToCart")}
                            </Button>
                            {justAdded && (
                                <Button variant="outlined" size="large" component={RouterLink} to={t("links.cart")}>
                                    {t("productPage.goToCart")}
                                </Button>
                            )}
                        </Box>

                        <Typography className={classes.descriptionTitle}>{t("productPage.detailsTitle")}</Typography>
                        <Box className={classes.description} dangerouslySetInnerHTML={{ __html: product.descriptionHtml }} />

                        {(product.material || product.dimensions) && (
                            <Box className={classes.specsRow}>
                                {product.material && (
                                    <Box>
                                        <Typography className={classes.specLabel}>{t("productPage.materialLabel")}</Typography>
                                        <Typography className={classes.specValue}>{product.material}</Typography>
                                    </Box>
                                )}
                                {product.dimensions && (
                                    <Box>
                                        <Typography className={classes.specLabel}>{t("productPage.dimensionsLabel")}</Typography>
                                        <Typography className={classes.specValue}>{product.dimensions}</Typography>
                                    </Box>
                                )}
                            </Box>
                        )}

                        <Box className={classes.shippingBox}>
                            <Typography className={classes.descriptionTitle}>{t("productPage.shippingInfoTitle")}</Typography>
                            <Typography className={classes.shippingText}>{t("productPage.shippingInfoText")}</Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default ProductDetailSection;
